import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gem, Sparkles, HeartPulse, Factory, Wheat, Globe } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'gem': Gem,
  'sparkles': Sparkles,
  'heart-pulse': HeartPulse,
  'factory': Factory,
  'wheat': Wheat,
  'globe': Globe,
};

export default function PortfolioPage() {
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    async function fetchSectors() {
      try {
        const { data } = await axios.get(`${API}/api/sectors`);
        setSectors(data);
      } catch (error) {
        console.error('Failed to fetch sectors:', error);
      }
    }
    fetchSectors();
  }, []);

  return (
    <div className="bg-white pt-32 pb-24">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Portfolio</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6">
            Our Investment Sectors
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            We focus on sectors where we can leverage our expertise, network, and long-term capital 
            to build market-leading positions across European markets.
          </p>
        </motion.div>
      </div>

      {/* Investment Philosophy */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-24">
        <div className="grid lg:grid-cols-3 gap-8">
          {[
            {
              title: 'Market Leaders',
              description: 'We invest in companies with strong market positions, defensible competitive advantages, and proven business models.'
            },
            {
              title: 'European Focus',
              description: 'Our portfolio is concentrated in European markets where we have deep knowledge, established relationships, and operational expertise.'
            },
            {
              title: 'Sustainable Growth',
              description: 'We partner with management teams to drive organic growth, strategic acquisitions, and operational improvements.'
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="p-8 border border-slate-200"
            >
              <h3 className="font-medium text-[#0A192F] mb-3">{item.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight">Sectors of Focus</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sectors.map((sector, index) => {
              const Icon = iconMap[sector.icon] || Globe;
              return (
                <motion.div
                  key={sector.sector_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid={`sector-${sector.sector_id}`}
                >
                  <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-serif text-xl text-[#0A192F] mb-3">{sector.name}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">{sector.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {sector.highlights.map((highlight, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-slate-100 text-xs text-slate-600"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Geographic Focus */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Geographic Focus</p>
              <h2 className="font-serif text-3xl text-[#0A192F] tracking-tight mb-6">
                European Markets
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our portfolio companies operate primarily in European markets, with a particular focus 
                on Germany, Switzerland, France, Italy, and the Benelux region.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                This geographic concentration allows us to maintain close relationships with management 
                teams, understand local market dynamics, and leverage our network to support growth 
                initiatives.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['Germany', 'Switzerland', 'France', 'Italy', 'Benelux', 'Austria'].map((country) => (
                  <div key={country} className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-2 h-2 bg-[#0A192F]" />
                    {country}
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1519677584237-752f8853252e?w=800&h=600&fit=crop"
                alt="European architecture"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Investment Criteria */}
      <section className="py-24 bg-[#0A192F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl text-white tracking-tight">Investment Criteria</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: 'Revenue', value: '€20M+' },
              { label: 'EBITDA Margin', value: '>10%' },
              { label: 'Geography', value: 'Europe' },
              { label: 'Holding Period', value: 'Long-term' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="font-serif text-3xl text-white mb-2">{item.value}</div>
                <div className="text-xs uppercase tracking-wider text-slate-400">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
